import React, { useCallback, useEffect, useState } from 'react';
import { CreditCard } from 'lucide-react';
import toast from 'react-hot-toast';
import { useColorTheme } from '../contexts/ColorThemeContext';
import { subscriptionService } from '../services/subscriptionService';
import { cn } from '../util/designUtil';
import { parseMonthlyPrice } from '../util/subscriptionPrice';

/**
 * Settings card where a star sets the monthly price fans pay to subscribe.
 * Self-contained: it loads and saves its own value through the backend.
 */
const SubscriptionPlanSection: React.FC = () => {
  const { theme } = useColorTheme();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [hasPlan, setHasPlan] = useState(false);
  const [priceInput, setPriceInput] = useState('');
  const [inputError, setInputError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const loadPlan = useCallback(async () => {
    setLoading(true);
    setLoadError(null);

    const result = await subscriptionService.getPlan();

    if (!result.success) {
      setLoadError(result.message || 'Could not load your subscription price');
      setHasPlan(false);
    } else if (result.monthlyPriceCredits !== null) {
      setPriceInput(String(result.monthlyPriceCredits));
      setHasPlan(true);
    } else {
      setHasPlan(false);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    void loadPlan();
  }, [loadPlan]);

  const handleSave = async () => {
    const parsed = parseMonthlyPrice(priceInput);
    if (!parsed.ok) {
      setInputError(parsed.reason);
      return;
    }

    setInputError(null);
    setSaving(true);

    const result = await subscriptionService.updatePlan(parsed.credits);

    if (result.success) {
      setPriceInput(String(parsed.credits));
      setHasPlan(true);
      toast.success(result.message || 'Monthly price saved');
    } else {
      toast.error(result.message || 'Failed to save your monthly price');
    }

    setSaving(false);
  };

  return (
    <div className="mt-8 bg-white border border-gray-200 rounded-lg shadow-sm">
      <div className="p-6 sm:p-8">
        <div className="flex items-center gap-2 mb-1">
          <CreditCard className={cn('h-5 w-5', theme.secondary)} />
          <h2 className="text-lg font-semibold text-gray-900">Subscriptions</h2>
        </div>
        <p className="text-sm text-gray-600">
          Set the monthly price in credits that fans pay to see your subscribers-only posts.
        </p>

        <div className="mt-4">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-gray-400"></div>
              Loading your monthly price...
            </div>
          ) : (
            <>
              {loadError && (
                <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs text-red-600 break-words">{loadError}</p>
                  <button
                    type="button"
                    onClick={() => void loadPlan()}
                    className="w-full sm:w-auto px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Retry
                  </button>
                </div>
              )}

              <label
                htmlFor="monthlyPriceCredits"
                className="block text-xs font-medium text-gray-700 mb-1"
              >
                Monthly price (credits)
              </label>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
                <input
                  id="monthlyPriceCredits"
                  type="text"
                  inputMode="numeric"
                  value={priceInput}
                  onChange={e => {
                    setPriceInput(e.target.value);
                    setInputError(null);
                  }}
                  placeholder="e.g. 10"
                  className={cn(
                    'w-full sm:flex-1 min-w-0 border rounded-md px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500',
                    inputError ? 'border-red-400' : 'border-gray-300',
                  )}
                />
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className={cn(
                    'w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2 text-white rounded-md text-sm font-semibold disabled:opacity-50',
                    theme.accent,
                  )}
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                      Saving...
                    </>
                  ) : (
                    'Save'
                  )}
                </button>
              </div>

              {inputError ? (
                <p className="text-xs text-red-600 mt-2 break-words">{inputError}</p>
              ) : (
                <p className="text-xs text-gray-500 mt-2">
                  {!hasPlan && !loadError && 'No monthly price set yet. '}
                  Whole credits only, at least 1 credit per month.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPlanSection;
